(function () {
  if (window.__unipacCartGiftInstalled) return;
  window.__unipacCartGiftInstalled = true;

  let syncTimer = null;

  /*
   * Every cart/gift operation is placed into this queue.
   *
   * IMPORTANT:
   * User clicks are NEVER discarded just because
   * a background sync is running.
   */
  let operationQueue = Promise.resolve();


  function getRootUrl() {
    if (
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
    ) {
      return window.Shopify.routes.root;
    }

    return '/';
  }


  /*
   * Put an operation into the queue.
   */
  function queueOperation(callback) {
    operationQueue = operationQueue
      .then(() => callback())
      .catch((error) => {
        console.error(
          'Cart gift operation error:',
          error
        );
      });

    return operationQueue;
  }


  /*
   * Schedule a background gift sync.
   *
   * Multiple rapid events collapse into one sync.
   */
  function scheduleSync(delay = 100) {
    clearTimeout(syncTimer);

    syncTimer = setTimeout(() => {
      queueOperation(() => syncGiftCore());
    }, delay);
  }


  async function getCart() {
    const response = await fetch(
      `${getRootUrl()}cart.js?_gift_sync=${Date.now()}`,
      {
        method: 'GET',

        headers: {
          Accept: 'application/json'
        },

        cache: 'no-store'
      }
    );

    if (!response.ok) {
      throw new Error(
        'Unable to retrieve cart.'
      );
    }

    return response.json();
  }


  function isGiftLine(item) {
    if (!item.properties) {
      return false;
    }

    return (
      item.properties._cart_gift === 'true' ||
      item.properties._cart_gift === true
    );
  }


  function getGiftLines(cart) {
    return cart.items.filter(
      (item) => isGiftLine(item)
    );
  }


  function getQualifyingSubtotal(cart) {
    return cart.items.reduce(
      (total, item) => {

        /*
         * Promotional gifts do not contribute
         * towards unlocking themselves.
         */
        if (isGiftLine(item)) {
          return total;
        }

        return total + item.final_line_price;
      },
      0
    );
  }


  function getModule() {
    return document.querySelector(
      '#cart-drawer cart-gift'
    );
  }


  function getConfig(module) {
    return {
      enabled:
        module.dataset.enabled === 'true',

      mode:
        module.dataset.mode || 'choose',

      threshold:
        Math.max(
          0,
          Math.round(
            parseFloat(
              module.dataset.threshold || '0'
            ) * 100
          )
        ),

      maxGifts:
        Math.max(
          1,
          parseInt(
            module.dataset.maxGifts || '1',
            10
          )
        ),

      currency:
        module.dataset.currency || 'AUD',

      lockedText:
        module.dataset.lockedText ||
        'Spend [amount] more to unlock your free gift.',

      unlockedText:
        module.dataset.unlockedText ||
        "You've unlocked your free gift!",

      chooseText:
        module.dataset.chooseText ||
        'Choose [x] out of [y] gifts. [selected]/[x] selected.'
    };
  }


  function getOptions(module) {
    return Array.from(
      module.querySelectorAll(
        '[data-gift-option]'
      )
    )
      .map((element) => ({
        element,

        variantId:
          Number(
            element.dataset.variantId
          ),

        available:
          element.dataset.available ===
          'true'
      }))
      .filter((option) => {
        return (
          option.available &&
          Number.isFinite(
            option.variantId
          )
        );
      });
  }


  function getRequiredCount(
    module,
    config
  ) {
    const totalOptions =
      getOptions(module).length;

    if (totalOptions === 0) {
      return 0;
    }

    return Math.min(
      config.maxGifts,
      totalOptions
    );
  }


  function formatMoney(
    cents,
    currency
  ) {
    try {
      return new Intl.NumberFormat(
        document.documentElement.lang ||
        'en-AU',
        {
          style: 'currency',
          currency: currency,

          minimumFractionDigits: 0,

          maximumFractionDigits:
            cents % 100 === 0
              ? 0
              : 2
        }
      ).format(
        cents / 100
      );

    } catch (error) {

      return `$${(
        cents / 100
      ).toFixed(2)}`;
    }
  }


  async function addGift(
    variantId
  ) {
    const response = await fetch(
      `${getRootUrl()}cart/add.js`,
      {
        method: 'POST',

        headers: {
          'Content-Type':
            'application/json',

          Accept:
            'application/json'
        },

        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,

              properties: {
                _cart_gift: 'true'
              }
            }
          ]
        })
      }
    );

    if (!response.ok) {
      const errorData =
        await response
          .json()
          .catch(() => ({}));

      throw new Error(
        errorData.description ||
        errorData.message ||
        'Unable to add gift.'
      );
    }

    return response.json();
  }


  async function changeGift(
    lineKey,
    quantity
  ) {
    const response = await fetch(
      `${getRootUrl()}cart/change.js`,
      {
        method: 'POST',

        headers: {
          'Content-Type':
            'application/json',

          Accept:
            'application/json'
        },

        body: JSON.stringify({
          id: lineKey,
          quantity: quantity
        })
      }
    );

    if (!response.ok) {
      throw new Error(
        'Unable to update gift.'
      );
    }

    return response.json();
  }


  async function removeGift(
    lineKey
  ) {
    return changeGift(
      lineKey,
      0
    );
  }


  /*
   * Use your theme's existing cart-items refresh.
   *
   * We deliberately avoid rebuilding the entire
   * drawer because that previously caused layout
   * problems.
   */
  async function refreshNativeCart() {
    const drawer =
      document.getElementById(
        'cart-drawer'
      );

    if (!drawer) return;

    const cartItems =
      drawer.querySelector(
        'cart-items'
      );

    if (
      cartItems &&
      typeof cartItems.refresh ===
      'function'
    ) {
      await cartItems.refresh();
      return;
    }

    /*
     * Fallback only.
     */
    if (
      typeof drawer.refresh ===
      'function'
    ) {
      await drawer.refresh(true);
    }
  }


  function updateCheckout(
    module,
    qualified,
    selectedCount,
    required
  ) {
    const drawer =
      document.getElementById(
        'cart-drawer'
      );

    if (!drawer) return;

    const checkout =
      drawer.querySelector(
        'button[name="checkout"]'
      );

    if (!checkout) return;

    const config =
      getConfig(module);

    const needsSelection =
      config.enabled &&
      config.mode === 'choose' &&
      qualified &&
      required > 0 &&
      selectedCount < required;


    if (needsSelection) {

      checkout.disabled = true;

      checkout.dataset.giftDisabled =
        'true';

    } else if (
      checkout.dataset.giftDisabled ===
      'true'
    ) {

      checkout.disabled = false;

      delete checkout.dataset
        .giftDisabled;
    }
  }


  function renderGift(
    module,
    cart
  ) {
    if (!module) return;

    const config =
      getConfig(module);


    if (
      !config.enabled ||
      cart.item_count === 0
    ) {
      module.hidden = true;
      return;
    }


    module.hidden = false;


    const subtotal =
      getQualifyingSubtotal(
        cart
      );


    const qualified =
      subtotal >=
      config.threshold;


    const remaining =
      Math.max(
        0,
        config.threshold -
        subtotal
      );


    let percentage = 100;


    if (config.threshold > 0) {
      percentage =
        Math.min(
          100,
          Math.max(
            0,
            (
              subtotal /
              config.threshold
            ) * 100
          )
        );
    }


    const message =
      module.querySelector(
        '[data-gift-message]'
      );


    const progress =
      module.querySelector(
        '[data-gift-progress]'
      );


    const current =
      module.querySelector(
        '[data-gift-current]'
      );


    const target =
      module.querySelector(
        '[data-gift-target]'
      );


    const selectionMessage =
      module.querySelector(
        '[data-gift-selection-message]'
      );


    if (progress) {
      progress.style.width =
        `${percentage}%`;
    }


    if (current) {
      current.textContent =
        formatMoney(
          subtotal,
          config.currency
        );
    }


    if (target) {
      target.textContent =
        formatMoney(
          config.threshold,
          config.currency
        );
    }


    if (message) {
      if (qualified) {

        message.textContent =
          config.unlockedText;

      } else {

        message.textContent =
          config.lockedText.replace(
            /\[amount\]/g,
            formatMoney(
              remaining,
              config.currency
            )
          );
      }
    }


    const giftLines =
      getGiftLines(cart);


    const selectedIds =
      giftLines.map(
        (gift) =>
          Number(
            gift.variant_id
          )
      );


    const selectedCount =
      giftLines.length;


    const options =
      getOptions(module);


    const totalOptions =
      options.length;


    const required =
      getRequiredCount(
        module,
        config
      );


    if (selectionMessage) {

      if (!qualified) {

        selectionMessage.textContent =
          '';

      } else if (
        config.mode === 'choose'
      ) {

        selectionMessage.textContent =
          config.chooseText
            .replace(
              /\[x\]/g,
              required
            )
            .replace(
              /\[y\]/g,
              totalOptions
            )
            .replace(
              /\[selected\]/g,
              selectedCount
            );

      } else {

        selectionMessage.textContent =
          required === 1
            ? 'Your gift will be added automatically.'
            : `${required} gifts will be added automatically.`;
      }
    }


    updateCheckout(
      module,
      qualified,
      selectedCount,
      required
    );


    options.forEach(
      (option) => {

        const selected =
          selectedIds.includes(
            option.variantId
          );


        option.element
          .classList
          .toggle(
            'is-selected',
            selected
          );


        option.element
          .classList
          .toggle(
            'is-locked',
            !qualified
          );


        const button =
          option.element
            .querySelector(
              '[data-gift-select]'
            );


        if (button) {

          /*
           * Do not overwrite an active
           * Adding... state.
           */
          if (
            button.dataset.giftPending ===
            'true'
          ) {
            return;
          }


          if (!qualified) {

            button.disabled = true;

            button.textContent =
              'Locked';

          } else if (selected) {

            button.disabled = false;

            button.textContent =
              'Selected ✓';

          } else if (
            selectedCount >=
            required
          ) {

            if (required === 1) {

              button.disabled = false;

              button.textContent =
                'Select gift';

            } else {

              button.disabled = true;

              button.textContent =
                'Limit reached';
            }

          } else {

            button.disabled = false;

            button.textContent =
              'Select gift';
          }
        }


        const autoLabel =
          option.element
            .querySelector(
              '[data-gift-auto-label]'
            );


        if (autoLabel) {

          autoLabel.textContent =
            selected
              ? 'Added ✓'
              : qualified
                ? 'Automatic gift'
                : 'Locked';
        }
      }
    );
  }


  async function enforceRules(
    module,
    cart
  ) {
    const config =
      getConfig(module);


    if (!config.enabled) {
      return false;
    }


    const subtotal =
      getQualifyingSubtotal(
        cart
      );


    const qualified =
      subtotal >=
      config.threshold;


    let giftLines =
      getGiftLines(cart);


    let changed = false;


    /*
     * Below threshold:
     * remove all promotional gifts.
     */
    if (!qualified) {

      for (
        const gift
        of giftLines
      ) {

        await removeGift(
          gift.key
        );

        changed = true;
      }

      return changed;
    }


    const options =
      getOptions(module);


    const validIds =
      options.map(
        (option) =>
          option.variantId
      );


    const required =
      getRequiredCount(
        module,
        config
      );


    /*
     * Remove old campaign gifts and
     * prevent quantity > 1.
     */
    for (
      const gift
      of giftLines
    ) {

      if (
        !validIds.includes(
          Number(
            gift.variant_id
          )
        )
      ) {

        await removeGift(
          gift.key
        );

        changed = true;

      } else if (
        gift.quantity > 1
      ) {

        await changeGift(
          gift.key,
          1
        );

        changed = true;
      }
    }


    if (changed) {

      cart =
        await getCart();

      giftLines =
        getGiftLines(cart);
    }


    /*
     * CHOOSE MODE
     */
    if (
      config.mode === 'choose'
    ) {

      if (
        giftLines.length >
        required
      ) {

        const excess =
          giftLines.slice(
            required
          );


        for (
          const gift
          of excess
        ) {

          await removeGift(
            gift.key
          );

          changed = true;
        }
      }
    }


    /*
     * AUTOMATIC MODE
     */
    if (
      config.mode ===
      'automatic'
    ) {

      const desired =
        options
          .slice(
            0,
            required
          )
          .map(
            (option) =>
              option.variantId
          );


      for (
        const gift
        of giftLines
      ) {

        if (
          !desired.includes(
            Number(
              gift.variant_id
            )
          )
        ) {

          await removeGift(
            gift.key
          );

          changed = true;
        }
      }


      if (changed) {

        cart =
          await getCart();

        giftLines =
          getGiftLines(cart);
      }


      const currentIds =
        giftLines.map(
          (gift) =>
            Number(
              gift.variant_id
            )
        );


      for (
        const variantId
        of desired
      ) {

        if (
          !currentIds.includes(
            variantId
          )
        ) {

          await addGift(
            variantId
          );

          changed = true;
        }
      }
    }


    return changed;
  }


  /*
   * Background sync.
   *
   * Unlike the old code, this does not block
   * or throw away user clicks.
   */
  async function syncGiftCore() {
    const module =
      getModule();

    if (!module) {
      return;
    }


    let cart =
      await getCart();


    const changed =
      await enforceRules(
        module,
        cart
      );


    if (changed) {

      await refreshNativeCart();

      cart =
        await getCart();
    }


    renderGift(
      getModule() || module,
      cart
    );
  }


  /*
   * ==================================================
   * SELECT GIFT
   * ==================================================
   *
   * Event delegation survives drawer/cart redraws.
   */
  document.addEventListener(
    'click',
    (event) => {

      const button =
        event.target.closest(
          '#cart-drawer [data-gift-select]'
        );


      if (!button) {
        return;
      }


      event.preventDefault();
      event.stopPropagation();


      /*
       * Prevent actual double-clicks while
       * preserving the FIRST click.
       */
      if (
        button.dataset.giftPending ===
        'true'
      ) {
        return;
      }


      const variantId =
        Number(
          button.dataset.variantId
        );


      if (!variantId) {
        return;
      }


      /*
       * Show feedback INSTANTLY.
       */
      button.dataset.giftPending =
        'true';

      button.disabled = true;

      button.textContent =
        'Adding...';


      /*
       * Cancel an unnecessary waiting
       * background sync.
       */
      clearTimeout(syncTimer);


      /*
       * The click goes into the queue.
       *
       * Even if a background sync is already
       * running, this click waits its turn
       * instead of being discarded.
       */
      queueOperation(
        async () => {

          try {

            const module =
              getModule();


            if (!module) {
              return;
            }


            const config =
              getConfig(module);


            if (
              !config.enabled ||
              config.mode !==
              'choose'
            ) {
              return;
            }


            let cart =
              await getCart();


            const subtotal =
              getQualifyingSubtotal(
                cart
              );


            /*
             * Customer somehow clicked while
             * they were below threshold.
             */
            if (
              subtotal <
              config.threshold
            ) {

              renderGift(
                module,
                cart
              );

              return;
            }


            const required =
              getRequiredCount(
                module,
                config
              );


            if (
              required === 0
            ) {
              return;
            }


            let gifts =
              getGiftLines(cart);


            const existing =
              gifts.find(
                (gift) =>
                  Number(
                    gift.variant_id
                  ) === variantId
              );


            /*
             * Clicking Selected ✓ removes it.
             */
            if (existing) {

              const currentButton =
                document.querySelector(
                  `#cart-drawer [data-gift-select][data-variant-id="${variantId}"]`
                );


              if (currentButton) {
                currentButton.textContent =
                  'Removing...';

                currentButton.disabled =
                  true;
              }


              await removeGift(
                existing.key
              );


              await refreshNativeCart();


              const latestCart =
                await getCart();


              renderGift(
                getModule(),
                latestCart
              );


              return;
            }


            /*
             * CHOOSE 1
             *
             * Selecting another product
             * swaps the old gift.
             */
            if (
              required === 1 &&
              gifts.length >= 1
            ) {

              for (
                const gift
                of gifts
              ) {

                await removeGift(
                  gift.key
                );
              }


              gifts = [];
            }


            /*
             * CHOOSE X
             *
             * Hard maximum.
             */
            if (
              gifts.length >=
              required
            ) {
              return;
            }


            /*
             * Add the selected gift.
             */
            await addGift(
              variantId
            );


            /*
             * Refresh the actual Shopify
             * cart contents immediately.
             */
            await refreshNativeCart();


            /*
             * Retrieve final cart after
             * Shopify discount processing.
             */
            const latestCart =
              await getCart();


            /*
             * Immediately update gift cards,
             * selection count and progress.
             */
            renderGift(
              getModule(),
              latestCart
            );


          } catch (error) {

            console.error(
              'Gift selection error:',
              error
            );


          } finally {

            /*
             * The drawer may have redrawn,
             * so find the CURRENT button.
             */
            const currentButton =
              document.querySelector(
                `#cart-drawer [data-gift-select][data-variant-id="${variantId}"]`
              );


            if (currentButton) {

              delete currentButton.dataset
                .giftPending;


              /*
               * Final render will normally set
               * correct button state.
               */
              currentButton.disabled =
                false;
            }


            /*
             * One small final reconciliation.
             */
            scheduleSync(120);
          }
        }
      );
    }
  );


  /*
   * ==================================================
   * CART EVENTS
   * ==================================================
   */


  /*
   * Normal product added.
   */
  document.addEventListener(
    'on:cart:add',
    () => {
      scheduleSync(100);
    }
  );


  /*
   * Add-to-cart drawer synchronised.
   */
  document.addEventListener(
    'on:cart:change',
    () => {
      scheduleSync(100);
    }
  );


  /*
   * Quantity changed or item removed.
   *
   * Your native cart-items.js fires this.
   */
  document.addEventListener(
    'on:line-item:change',
    () => {
      scheduleSync(80);
    }
  );


  /*
   * Drawer manually refreshed.
   */
  document.addEventListener(
    'dispatch:cart-drawer:refresh',
    () => {
      scheduleSync(150);
    }
  );


  /*
   * ==================================================
   * DRAWER OBSERVER
   * ==================================================
   *
   * Only react when a NEW gift module itself
   * is inserted.
   *
   * The old observer reacted to almost every
   * DOM change inside the cart, creating many
   * unnecessary background syncs.
   */
  function installObserver() {
    const drawer =
      document.getElementById(
        'cart-drawer'
      );


    if (
      !drawer ||
      drawer.__giftObserverInstalled
    ) {
      return;
    }


    drawer.__giftObserverInstalled =
      true;


    const observer =
      new MutationObserver(
        (mutations) => {

          let newGiftModule =
            false;


          for (
            const mutation
            of mutations
          ) {

            for (
              const node
              of mutation.addedNodes
            ) {

              if (
                node.nodeType !== 1
              ) {
                continue;
              }


              if (
                node.matches &&
                node.matches(
                  'cart-gift'
                )
              ) {

                newGiftModule = true;
                break;
              }


              if (
                node.querySelector &&
                node.querySelector(
                  'cart-gift'
                )
              ) {

                newGiftModule = true;
                break;
              }
            }


            if (newGiftModule) {
              break;
            }
          }


          if (newGiftModule) {
            scheduleSync(80);
          }
        }
      );


    observer.observe(
      drawer,
      {
        childList: true,
        subtree: true
      }
    );
  }


  function start() {
    installObserver();

    scheduleSync(30);
  }


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      start,
      {
        once: true
      }
    );

  } else {

    start();
  }


  /*
   * Shopify Theme Editor may reload
   * the drawer section.
   */
  document.addEventListener(
    'shopify:section:load',
    () => {

      const drawer =
        document.getElementById(
          'cart-drawer'
        );


      if (drawer) {
        drawer.__giftObserverInstalled =
          false;
      }


      installObserver();

      scheduleSync(80);
    }
  );

})();